import { type RefObject, useCallback, useRef } from "react";

import { faFilePdf } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	COMPETENCY_IMPORT_FILE_TOO_LARGE_COPY,
	COMPETENCY_IMPORT_UNSUPPORTED_TYPE_COPY,
	MAX_COMPETENCY_IMPORT_BYTES,
} from "@performyard/service-contracts";

import { startCompetencyImport } from "../../../api/services/defs/internal-api/ai";
import { ErrorResponse } from "../../../api/services/defs/internal-api/internal-base-request";

import { useAskPerformYardStore } from "../AskPerformYardStore";

const KICKOFF_ERROR =
	"We couldn't read this file. Try a different PDF, or paste your competency information directly into the chat.";

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${Math.round(kb)} KB`;
	return `${(kb / 1024).toFixed(1)} MB`;
};

type UseCompetencyDocumentUpload = {
	fileInputRef: RefObject<HTMLInputElement | null>;
	triggerUpload: () => void;
	handleFileSelected: (file: File) => Promise<void>;
};

export const useCompetencyDocumentUpload = (): UseCompetencyDocumentUpload => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const triggerUpload = useCallback(() => fileInputRef.current?.click(), []);

	const handleFileSelected = useCallback(async (file: File) => {
		const {
			addAttachmentUserMessage,
			updateAttachmentState,
			scrollToBottom,
			setCompetencyUploadPromptMessage,
			addStaticAssistantMessage,
			addImportNarrationMessage,
		} = useAskPerformYardStore.getState();
		setCompetencyUploadPromptMessage(null);
		const surfaceUploadError = (errorText: string) => {
			setCompetencyUploadPromptMessage(addStaticAssistantMessage(errorText));
			scrollToBottom?.();
		};
		const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
		if (!isPdf) {
			addAttachmentUserMessage({ state: "error", icon: faFilePdf, title: file.name });
			surfaceUploadError(COMPETENCY_IMPORT_UNSUPPORTED_TYPE_COPY);
			return;
		}
		if (file.size > MAX_COMPETENCY_IMPORT_BYTES) {
			addAttachmentUserMessage({ state: "error", icon: faFilePdf, title: file.name });
			surfaceUploadError(COMPETENCY_IMPORT_FILE_TOO_LARGE_COPY);
			return;
		}
		const id = addAttachmentUserMessage({
			state: "uploading",
			icon: faFilePdf,
			title: file.name,
			description: "Uploading your competency document…",
		});
		scrollToBottom?.();
		try {
			const { taskId } = await startCompetencyImport(file);
			const narrationMessageId = addImportNarrationMessage("Analyzing your document…");
			updateAttachmentState(id, {
				importTaskId: taskId,
				state: "done",
				description: `PDF · ${formatFileSize(file.size)}`,
				narrationMessageId,
			});
			scrollToBottom?.();
		} catch (err) {
			updateAttachmentState(id, { state: "error", description: undefined });
			surfaceUploadError(err instanceof ErrorResponse && err.message ? err.message : KICKOFF_ERROR);
		}
	}, []);

	return { fileInputRef, triggerUpload, handleFileSelected };
};
